import { Grid, Typography, Card, Button, Tooltip, Chip } from '@mui/material';
import PropTypes from 'prop-types';
import ArrowBack from '@mui/icons-material/ArrowBack';
import HeightIcon from '@mui/icons-material/Height';
import PlusIcon from '@mui/icons-material/Add';
import MinusIcon from '@mui/icons-material/Remove';
import SelectDropdown from '../../../components/SelectDropdown';
import DatePickerMui from '../../../components/DatePickerMui';
import { ShimmerComponent } from '../../../components/Shimmer/ShimmerComponent';
import BouncingLogo from '../../../components/Shimmer/BouncingLogo';
import { ROUTES } from '../../../constants/routes';
import { ADD_FINANCE_OFFER, REMOVE_FINANCE_OFFER } from '../constants';
import styles from './styles';

// Helper function to determine minimum end date
export const calculateMinEndDate = (promotionData, currentDate, minEndDate) => {
  if (promotionData?.status === 'FROZEN' && promotionData?.status === 'ARCHIVED') {
    return currentDate;
  }
  if (promotionData?.start_date) {
    return new Date(promotionData.start_date);
  }
  return minEndDate;
};

// Helper component for loading state
export const LoadingShimmer = () => (
  <ShimmerComponent
    updatedStyle={{
      width: '90%',
      height: '100%',
      backgroundSize: '2000px 800px',
      border: '0px',
      margin: 'auto',
      padding: '0 20px',
    }}
  >
    <BouncingLogo height="88vh" text="Loading..." />
  </ShimmerComponent>
);

// Helper component for back button
export const BackButton = ({ navigate }) => (
  <section>
    <Button
      startIcon={<ArrowBack sx={{ color: 'black' }} />}
      onClick={() => navigate(ROUTES.promotions)}
      sx={{ display: 'flex', padding: '0px 20px', color: 'black', fontWeight: 700 }}
    >
      Back
    </Button>
  </section>
);

// Helper component for promotion dates section
export const PromotionDatesSection = ({
  promotionData,
  isCardEnabled,
  isAdmin,
  disableDateSelection,
  isDateEditable,
  currentDate,
  selectedStartDate,
  selectedEndDate,
  handleStartDateChange,
  handleEndDateChange,
  minEndDate,
}) => {
  const sectionStyle = promotionData?.promotion_type
    ? { ...styles.sectionSpacing }
    : { ...styles.sectionSpacing, paddingTop: '0' };

  return (
    <section style={sectionStyle}>
      <Typography sx={styles.heading}>Promotions Dates</Typography>
      <Card sx={isCardEnabled ? styles.cardBox : { ...styles.disableCard, ...styles.cardBox }}>
        <Grid container spacing={1} sx={{ padding: '20px' }}>
          <Grid item className="promotion-start-dt" sx={{ width: 'calc(100%/2)' }}>
            <DatePickerMui
              label={'Start Date'}
              handleDateChange={handleStartDateChange}
              disabled={!isAdmin || disableDateSelection}
              defaultValue={null}
              disablePast={isDateEditable}
              sx={{ width: 'calc(100% - 150px/2)' }}
              minDate={currentDate}
              updateDateValue={selectedStartDate}
            />
          </Grid>
          <Grid item className="promotion-end-dt" sx={{ width: 'calc(100%/2)' }}>
            <DatePickerMui
              label={'End Date'}
              handleDateChange={handleEndDateChange}
              passUnformattedDate={true}
              disabled={!isAdmin || disableDateSelection}
              defaultValue={null}
              minDate={calculateMinEndDate(promotionData, currentDate, minEndDate)}
              disablePast
              sx={{ width: 'calc(100% - 150px/2)' }}
              updateDateValue={selectedEndDate}
            />
          </Grid>
        </Grid>
      </Card>
    </section>
  );
};

// Helper component for branches section
export const BranchesSection = ({
  promotionData,
  shouldShowStoreDropdown,
  storeOptions,
  selectedStores,
  handleStoreChange,
  mode,
  headlineLoading,
}) => {
  const sectionStyle =
    promotionData?.status === 'ARCHIVED'
      ? { ...styles.disableCard, ...styles.sectionSpacing }
      : styles.sectionSpacing;

  return (
    <section style={sectionStyle}>
      <Typography style={styles.heading}>Branches</Typography>
      <Card sx={styles.cardBox} style={{ overflow: 'visible' }}>
        {shouldShowStoreDropdown && (
          <SelectDropdown
            isSelectAll={true}
            multiple={true}
            options={storeOptions}
            label={'Please Select all the branches this offers applies to'}
            multiSelectDefaultValues={
              selectedStores?.length > 0 ? selectedStores : [promotionData?.store_id]
            }
            handleFilterChange={handleStoreChange}
            updateMultipleDropdownValue={
              selectedStores?.length > 0 ? selectedStores : [promotionData?.store_id]
            }
            width={'99%'}
            disabled={mode !== 'Create' || headlineLoading}
          />
        )}
      </Card>
    </section>
  );
};

// Helper component for finance offer button
export const FinanceOfferButton = ({ promotionData, handleAddFinanceOffer }) => {
  const hasDiscountOffer =
    promotionData?.discount_offer !== undefined && promotionData?.discount_offer !== null;
  const hasTwoFinanceOffers = promotionData?.finance_offer?.length === 2;

  return (
    <Tooltip
      arrow
      placement="top"
      leaveDelay={500}
      sx={{ popper: styles.freezeDateTooltip }}
      title={hasDiscountOffer ? 'Cannot add finance offer when discount offer is present' : ''}
    >
      <span>
        <Button
          sx={{
            ...styles.buttonStyle,
            background: '#e0e0e0',
            color: '#000',
            width: 'fit-content',
          }}
          disabled={hasDiscountOffer}
          startIcon={hasTwoFinanceOffers ? <MinusIcon /> : <PlusIcon />}
          onClick={handleAddFinanceOffer}
        >
          {hasTwoFinanceOffers ? REMOVE_FINANCE_OFFER : ADD_FINANCE_OFFER}
        </Button>
      </span>
    </Tooltip>
  );
};

// Helper component for preview header
export const PreviewHeader = ({ status, canShowFlipButton, flipOffer, handleFlipButtonClick }) => (
  <Grid container sx={{ marginBottom: '15px' }} justifyContent="space-between">
    <Grid item xs={5}>
      <Typography sx={{ ...styles.heading, ...styles.promoPreview }}>Promotion Preview</Typography>
    </Grid>
    <Grid item xs={7} container justifyContent={canShowFlipButton ? 'space-between' : 'flex-end'}>
      <Grid item sx={{ width: 'fit-content', paddingRight: '15px' }}>
        {canShowFlipButton && (
          <Tooltip
            arrow
            placement="top"
            leaveDelay={500}
            sx={{ popper: styles.popper }}
            style={{ marginTop: '10px' }}
            title={`Flip to lead with ${flipOffer ? 'discount' : 'financing'} offer`}
          >
            <Button
              variant="contained"
              color="primary"
              onClick={handleFlipButtonClick}
              startIcon={<HeightIcon />}
            >
              Flip offer
            </Button>
          </Tooltip>
        )}
      </Grid>
      <Grid
        item
        container
        sx={{ width: 'fit-content' }}
        style={{ marginTop: '10px' }}
        justifyContent="flex-end"
        alignItems="center"
      >
        <Typography>Status: &#x200A;&#x200D; </Typography>
        <Chip sx={styles[status?.toLowerCase()]} label={status} />
      </Grid>
    </Grid>
  </Grid>
);

// PropTypes
BackButton.propTypes = {
  navigate: PropTypes.func.isRequired,
};

PromotionDatesSection.propTypes = {
  promotionData: PropTypes.object,
  isCardEnabled: PropTypes.bool.isRequired,
  isAdmin: PropTypes.bool.isRequired,
  disableDateSelection: PropTypes.bool.isRequired,
  isDateEditable: PropTypes.bool.isRequired,
  currentDate: PropTypes.instanceOf(Date).isRequired,
  selectedStartDate: PropTypes.string.isRequired,
  selectedEndDate: PropTypes.string.isRequired,
  handleStartDateChange: PropTypes.func.isRequired,
  handleEndDateChange: PropTypes.func.isRequired,
  minEndDate: PropTypes.oneOfType([PropTypes.string, PropTypes.instanceOf(Date)]).isRequired,
};

BranchesSection.propTypes = {
  promotionData: PropTypes.object,
  shouldShowStoreDropdown: PropTypes.bool.isRequired,
  storeOptions: PropTypes.arrayOf(PropTypes.object).isRequired,
  selectedStores: PropTypes.array.isRequired,
  handleStoreChange: PropTypes.func.isRequired,
  mode: PropTypes.string.isRequired,
  headlineLoading: PropTypes.bool.isRequired,
};

FinanceOfferButton.propTypes = {
  promotionData: PropTypes.object,
  handleAddFinanceOffer: PropTypes.func.isRequired,
};

PreviewHeader.propTypes = {
  status: PropTypes.string.isRequired,
  canShowFlipButton: PropTypes.bool.isRequired,
  flipOffer: PropTypes.bool.isRequired,
  handleFlipButtonClick: PropTypes.func.isRequired,
};
